import React, { useContext } from "react";
import { graphql, useFragment } from "react-relay";

import { useFeatureIsOn } from "@growthbook/growthbook-react";
import cx from "classnames";

// eslint-disable-next-line max-len
import { useWillBeRecordedNoCacheQuery } from "~/account/Copilot/AutoRecordRules/modal/WillBeRecordedNoCache";
import { AppContext } from "~/common";
import { inVideocallMode } from "~/lib/utils";
import { useMeetingModalContext } from "~/meetings/MeetingModal/MeetingModalContext";
import AskFellowPanel from "~/nav/panels/AskFellow";
import MeetingPhaseContext from "~/stream/MeetingPhaseContext";
import { Modal } from "~/ui/legacyFellowIndex";
import { useModals } from "~/ui/modals";

import { DESKTOP_BOTLESS_RECORDING_FEATURE_FLAG, WEB_BOTLESS_RECORDING_FEATURE_FLAG } from "../featureFlag";
import FloatingAskFellowButton from "./FloatingAskFellowButton";
import InMeetingControls from "./InMeetingControls";
import PreMeetingControls from "./PreMeetingControls";

import type { FloatingRecordingControls_note$key } from "~/__generated__/FloatingRecordingControls_note.graphql";
import type { PreMeetingControls_note$key } from "~/__generated__/PreMeetingControls_note.graphql";
import type { PreMeetingControls_viewer$key } from "~/__generated__/PreMeetingControls_viewer.graphql";

import noteCardStyles from "~/stream/css/NoteCard.module.css";

import styles from "./FloatingRecordingControls.module.css";

export const FLOATING_RECORDING_CONTROLS_FEATURE = "floating-recording-controls";
export const FLOATING_RECORDING_CONTROLS_EXPERIMENT_KEY = "floating-recording-controls-experiment";

const FloatingRecordingControlsFragment = graphql`
	fragment FloatingRecordingControls_note on Note {
		id
		...PreMeetingControls_note
		...AskFellowHooks_useAskFellowButton_note
	}
`;

interface FloatingRecordingControlsProps {
	viewer: PreMeetingControls_viewer$key;
	note: PreMeetingControls_note$key;
	isFirstNote?: boolean;
}

function FloatingRecordingControls({ viewer, note }: FloatingRecordingControlsProps) {
	const { copilotStatus, creator } = useContext(MeetingPhaseContext);

	switch (copilotStatus.type) {
		case "not-recording":
		case "scheduled": {
			return <PreMeetingControls viewer={viewer} note={note} />;
		}
		default: {
			return <InMeetingControls copilotStatus={copilotStatus} creator={creator} />;
		}
	}
}

interface FloatingRecordingControlsWrapperProps {
	viewer: PreMeetingControls_viewer$key;
	note: FloatingRecordingControls_note$key;
	isFirstNote?: boolean;
	eventGuid: string | null;
}

export default function FloatingRecordingControlsWrapper({
	viewer,
	note: noteKey,
	isFirstNote = true,
	eventGuid,
}: FloatingRecordingControlsWrapperProps) {
	const desktopRecordingEnabled = useFeatureIsOn(DESKTOP_BOTLESS_RECORDING_FEATURE_FLAG);
	const microphoneRecordingEnabled = useFeatureIsOn(WEB_BOTLESS_RECORDING_FEATURE_FLAG);
	const { user } = useContext(AppContext);
	const note = useFragment(FloatingRecordingControlsFragment, noteKey);
	const meetingModalContext = useMeetingModalContext();
	const { isAskFellowOpen, onOpenAskFellow } = meetingModalContext ?? {};
	const modals = useModals();

	// It would be great to hoist these hooks to the parent component one day...
	// https://github.com/fellowapp/fellow/pull/13832#discussion_r2006359459
	const {
		noMeeting,
		copilotStatus: { type: copilotStatusType },
	} = useContext(MeetingPhaseContext);
	const willBeRecorded = useWillBeRecordedNoCacheQuery(eventGuid);
	const currentlyRecording = copilotStatusType === "recording" || copilotStatusType === "paused";

	const openAskFellowInExtension = React.useCallback(() => {
		modals.open(({ close }) => (
			<Modal onClose={close} className={styles.askFellowModal}>
				<Modal.Body className={styles.askFellowModalBody}>
					<AskFellowPanel noteId={note.id} compactLandingPage={true} onClose={close} />
				</Modal.Body>
			</Modal>
		));
	}, [modals, note.id]);

	if (
		(noMeeting && !currentlyRecording && !desktopRecordingEnabled && !microphoneRecordingEnabled) ||
		willBeRecorded === "RESTRICTED"
	) {
		return null;
	}

	return (
		<div
			className={cx("invert-theme", styles.floatingContainer, {
				[noteCardStyles.showOnlyOnFocusWithin]:
					!isFirstNote && (copilotStatusType === "not-recording" || copilotStatusType === "scheduled"),
			})}
		>
			<div data-tour-tooltip-target="floating-recording-controls" className={styles.tourtipTarget}>
				{meetingModalContext ? (
					inVideocallMode ? null : (
						<>{!isAskFellowOpen && <FloatingAskFellowButton note={note} onStartConversation={onOpenAskFellow} />}</>
					)
				) : (
					<>
						{user?.uxSimplificationEnabled && (
							<FloatingAskFellowButton
								note={note}
								onStartConversation={inVideocallMode ? openAskFellowInExtension : undefined}
							/>
						)}
						<FloatingRecordingControls viewer={viewer} note={note} isFirstNote={isFirstNote} />
					</>
				)}
			</div>
		</div>
	);
}
